import {
    useReviewsPageDataContext,
    useReviewsPageQueryContext,
    useReviewsPageSelectionContext,
    useReviewsPageTransportContext,
} from '@/context/reviews-page.context';
import { useAuth } from '@/context/auth.context';
import { useBrandsTableAnalysis } from '@/components/brands-table/use-brands-table-analysis';
import { useBrandsGroupEditor } from '@/components/brands-table/use-brands-group-editor';
import { useBrandsTableSelection } from '@/components/brands-table/use-brands-table-selection';
import { BrandsTableData, useBrandsTableViewModel } from '@/components/brands-table/use-brands-table-view-model';
import { useSelectionBrandsAction } from '@/components/action-buttons/use-selection-brands-action';
import { extractApiErrorMessage } from '@/components/group-editor/extract-api-error-message';
import { createBrandsTableColumns } from '@/components/brands-table/brands-table-columns';
import {
    buildBrandsTableControllerBindings,
    type BrandsTableControllerBindings,
} from '@/components/brands-table/brands-table-controller-bindings.builder';
import { buildGroupTableControllerCore } from '@/components/group-tables/build-group-table-controller-core';

export type UseBrandsTableControllerArgs = {
    brandsPage: number;
    brandsOnPage: number;
    setBrandsPage: (page: number) => void;
    setBrandsPageCount: (count: number) => void;
    setBrandsTotalRows: (count: number) => void;
};

export type BrandsTableControllerValue = BrandsTableControllerBindings;

export const useBrandsTableController = ({
    brandsPage,
    brandsOnPage,
    setBrandsPage,
    setBrandsPageCount,
    setBrandsTotalRows,
}: UseBrandsTableControllerArgs): BrandsTableControllerValue => {
    const { brandGroups } = useReviewsPageDataContext();
    const { pickedBrandGroups, setPickedBrandGroups } = useReviewsPageSelectionContext();
    const { maxSelectedItems } = useReviewsPageQueryContext();
    const { getBrandGroups, getProducts } = useReviewsPageTransportContext();
    const { accessToken, clientId } = useAuth();
    const {
        nameFilter,
        setNameFilter,
        brandFilter,
        setBrandFilter,
        sort,
        filteredAndSortedBrandGroups,
        pagedBrandGroups,
        onSortChanged,
    } = useBrandsTableViewModel({
        clientId,
        brandGroups,
        brandsPage,
        brandsOnPage,
        setBrandsPage,
        setBrandsPageCount,
        setBrandsTotalRows,
    });
    const { onHeaderCheckboxChange, onCheckboxChange, isHeaderChecked, checkboxData } = useBrandsTableSelection({
        pickedBrandGroups,
        setPickedBrandGroups,
        maxSelectedItems,
        visibleBrandGroups: filteredAndSortedBrandGroups,
        pagedBrandGroups,
    });
    const { onAllPickedBrandGroups } = useSelectionBrandsAction({
        hasPickedBrandGroups: pickedBrandGroups.length > 0,
        brandGroups: filteredAndSortedBrandGroups,
        maxSelectedItems,
        setPickedBrandGroups,
    });
    const { onBrandGroupClick, ...analysisBindings } = useBrandsTableAnalysis<BrandsTableData>({
        accessToken,
    });
    const { onGroupEditorOpen, ...groupEditorBindings } = useBrandsGroupEditor<BrandsTableData>({
        accessToken,
        getBrandGroups,
        getProducts,
        extractApiErrorMessage,
    });
    const { columns, headerSecondaryRow, groupEditorColumns } = createBrandsTableColumns({
        pickedBrandGroups,
        isHeaderChecked,
        onHeaderCheckboxChange,
        nameFilter,
        setNameFilter,
        brandFilter,
        setBrandFilter,
        onGroupEditorOpen,
    });

    return buildGroupTableControllerCore({
        table: {
            columns,
            data: pagedBrandGroups,
            rowKey: 'id',
            tableId: 'brands-groups-table',
            tableScope: 'brands',
            onSortChanged,
            currentSort: sort,
            pickedData: pickedBrandGroups,
            onAllPicked: onAllPickedBrandGroups,
            onHeaderCheckboxChange,
            onCheckboxChange,
            isHeaderChecked,
            checkboxData,
            headerSecondaryRow,
            onRowClick: onBrandGroupClick,
        },
        analysis: analysisBindings,
        groupEditor: {
            ...groupEditorBindings,
            groupEditorColumns,
        },
        buildControllerBindings: buildBrandsTableControllerBindings,
    });
};
